/* External */
import {
  memo, useEffect, useCallback, useState, useRef, useMemo,
} from 'react';
import PropTypes from 'prop-types';
import { withFocusable } from '@noriginmedia/react-spatial-navigation';
import { useLocation, useHistory, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';

/* Ott-common */
import CardCreate from '@dtvgo/card-create-profile';

/* Other */
import { getJsonImage } from 'src/theme/images';

/* Components */
import FocusableButtonProfile from '../../../../../components/FocusableButtonProfile';
import FocusableEditNameProfile from '../../../../../components/FocusableEditNameProfile';

/* Utils */
import { colors, TYPE_ACTIONS_PROFILE } from '../../../../../utils/constants';
import { Routes, getLink } from '../../../../../utils/router';
import { useCountry } from '../../../../../providers/ConfigProvider';
import { useGtmLocalContext } from '../../../../../providers/GtmLocalProvider';
import { useOnBackPress } from '../../../../../utils/keyDownEvents';
import { getInitials } from '../../../../../utils/profiles';

/* GTM */
import { GTM_CATEGORIES, GTM_EVENT_NAMES, GTM_SUBCATEGORIES } from '../../../../../utils/googleTags';

/* Assets */
import chevronLeft from '../../../../../assets/svg/chevron-left.svg';

/* Storage */
import { createProfile } from '../../../../../storage/profiles/actions';
import { createProfileAPI, getProfileAPI } from '../../../../../services/profiles';

/* Style */
import './DefaultCreateProfile.scss';
import { useLoading } from '../../../../../providers/LoadingProvider';

function DefaultCreateProfile({ realFocusKey, setFocus, typeAction }) {
  const nameProfileFocusKey = `${realFocusKey}-default-name-profile`;
  const history = useHistory();
  const location = useLocation();
  const params = useParams();
  const dispatch = useDispatch();
  const userProfiles = useSelector((state) => state.profile.profiles) ?? JSON.parse(localStorage.getItem('profiles'));
  const [profiles] = useState(userProfiles);
  const [selectedProfile, setSelectedProfile] = useState({});
  const [cardProfileName, setCardProfileName] = useState(location.state?.username);
  const cardColor = params.id
    ? colors[location.state?.indexColor]
    : colors[(profiles?.length || 0) + 1];
  const buttonDelete = !selectedProfile?.isMainProfile && selectedProfile?.id;
  const country = useCountry();
  const { commonTaggingEvent } = useGtmLocalContext();
  const tagsMountRef = useRef(true);
  const loading = useRef(true);
  const [loadingPage, setLoadingPage] = useState(true);
  const { hideLoading, showLoading } = useLoading();
  const [buttonLoading, setButtonLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const isCreatingProfile = useRef(false);

  useEffect(() => {
    let timeout = setTimeout(() => {
      timeout = undefined;
      showLoading();
    }, 0);
    return () => {
      if (timeout) {
        clearTimeout(timeout);
        timeout = undefined;
      } else {
        hideLoading();
      }
    };
  }, [hideLoading, showLoading]);

  useEffect(() => {
    setTimeout(() => {
      hideLoading();
    }, 1000);
    clearTimeout();
  }, [hideLoading]);

  useEffect(() => {
    if (params.id) {
      setLoadingPage(true);
      getProfileAPI(params.id).then((res) => {
        setSelectedProfile(res.data);
        setCardProfileName(res.data.name);
        loading.current = !loading.current;
        setLoadingPage(false);
        return undefined;
      }).catch((error) => {
        setLoadingPage(false);
        history.push(
          getLink(Routes.Private.Profiles.Error),
          {
            create: false,
            source: 'MIDDLEWARE',
            location: 'PROFILE_EDIT',
            type: 'UNEXPECTED',
            externCode: error?.response?.status,
            typeError: TYPE_ACTIONS_PROFILE.EDIT,
          },
        );
      });

      if (isEdit) {
        history.push(getLink(Routes.Private.Profiles), { edit: true });
      }
    } else {
      setLoadingPage(false);
    }
  }, [history, params.id, isEdit]);

  const gtmSubCategory = useMemo(() => {
    if (typeAction === TYPE_ACTIONS_PROFILE.EDIT) {
      return GTM_SUBCATEGORIES.PROFILE.PROFILES_EDIT;
    }
    return GTM_SUBCATEGORIES.PROFILE.PROFILES_CREATE;
  }, [typeAction]);

  const profileCommonTagEvent = useCallback(({ eventName }) => {
    if (eventName) {
      commonTaggingEvent({
        category: GTM_CATEGORIES.PROFILES_DATA,
        subCategory: gtmSubCategory,
        eventName,
      });
    }
  }, [commonTaggingEvent, gtmSubCategory]);

  useEffect(() => {
    if (tagsMountRef.current && country) {
      profileCommonTagEvent({ eventName: GTM_EVENT_NAMES.PAGE_VIEW });
      tagsMountRef.current = false;
    }
  }, [profileCommonTagEvent, country]);

  useEffect(() => {
    setFocus(nameProfileFocusKey);
  }, [nameProfileFocusKey, setFocus]);

  const handleEditProfile = useCallback(({ id, indexColor }) => {
    profileCommonTagEvent({ eventName: GTM_EVENT_NAMES.PROFILES_EDIT.EDIT_NAME_PROFILE });
    history.push(
      getLink(Routes.Private.Profiles.Edit.Name, { id }),
      {
        indexColor,
        typeCreateName: TYPE_ACTIONS_PROFILE.EDIT,
      },
    );
  }, [history, profileCommonTagEvent]);

  const handleDeleteProfile = useCallback(({ id, indexColor }) => {
    profileCommonTagEvent({ eventName: GTM_EVENT_NAMES.PROFILES_EDIT.DELETE_CREATE_PROFILE });
    history.push(getLink(Routes.Private.Profiles.Delete, { id }), { indexColor });
  }, [history, profileCommonTagEvent]);

  const clickCreateProfile = useCallback(() => {
    setButtonLoading(true);
    if (!isCreatingProfile.current) {
      isCreatingProfile.current = true;
      profileCommonTagEvent({ eventName: GTM_EVENT_NAMES.PROFILES_CREATE.SELECT_CREATE_PROFILE });
      createProfileAPI({
        name: location.state?.username,
        avatar: '',
        ageRating: '18',
      }).then((res) => {
        dispatch(createProfile(res.data.profiles));
        localStorage.setItem('profiles', JSON.stringify(res.data.profiles));
        history.push(getLink(Routes.Private.Profiles), { create: true });
        setButtonLoading(false);
        return true;
      })
        .catch((error) => {
          console.error(error);
          history.push(
            getLink(Routes.Private.Profiles.Error),
            {
              create: false,
              source: 'MIDDLEWARE',
              location: 'PROFILE_CREATE',
              type: 'UNEXPECTED',
              externCode: error?.response?.status,
              typeError: TYPE_ACTIONS_PROFILE.CREATE,
            },
          );
          return false;
        });
    }
  }, [dispatch, history, location, profileCommonTagEvent]);

  const clickConfirm = useCallback(() => {
    setButtonLoading(true);
    profileCommonTagEvent({ eventName: GTM_EVENT_NAMES.PROFILES_EDIT.CONFIRM_CREATE_PROFILE });
    if (params) {
      setButtonLoading(false);
      setIsEdit(true);
    } else {
      createProfileAPI({
        name: location.state?.username,
        avatar: '',
        ageRating: '18',
      }).then((res) => {
        dispatch(createProfile(res.data.profiles));
        history.push(getLink(Routes.Private.Profiles), { create: true });
        setButtonLoading(false);
        return true;
      })
        .catch((error) => {
          console.error(error);
          history.push(getLink(Routes.Private.Profiles.Error), {
            source: 'MIDDLEWARE',
            location: 'PROFILE_CREATE',
            type: 'UNEXPECTED',
            externCode: error?.response?.status,
            typeError: TYPE_ACTIONS_PROFILE.CREATE,
          });
          return false;
        });
    }
  }, [dispatch, history, location, params, profileCommonTagEvent]);

  const editProfileName = useCallback(() => {
    profileCommonTagEvent({ eventName: GTM_EVENT_NAMES.PROFILES_CREATE.EDIT_NAME_PROFILE });
    history.push(
      getLink(Routes.Private.Profiles.Create.Name),
      { typeCreateName: TYPE_ACTIONS_PROFILE.CREATE },
    );
  }, [history, profileCommonTagEvent]);

  useOnBackPress({
    listener: () => {
      const eventName = (typeAction === TYPE_ACTIONS_PROFILE.EDIT)
        ? GTM_EVENT_NAMES.PROFILES_EDIT.BACK_PROFILES
        : GTM_EVENT_NAMES.PROFILES_CREATE.BACK_PROFILES_CREATE;
      profileCommonTagEvent({
        eventName,
      });
      history.goBack();
    },
  });

  if (loadingPage) {
    return (
      <div className="dtv-html5-c-user-default-profile-create">
        <div className="dtv-html5-c-user-profile--loading" />
      </div>
    );
  }

  // This regex returns the first 2 letters of the selected profile name
  const userNameSplit = getInitials(cardProfileName);

  return (
    <div className="dtv-html5-c-user-default-profile-create">
      <div className="dtv-html5-c-user-default-profile-create--div">
        <div className="dtv-html5-c-title__back">
          <img
            src={getJsonImage('chevron-left') || chevronLeft}
            alt="Chevron Left"
            className="dtv-html5-c-title__back--chevronLeft"
          />
          {selectedProfile?.id
            ? (
              <h1><FormattedMessage id="profile.change" /></h1>
            ) : (
              <h1><FormattedMessage id="profile.create" /></h1>
            )}
        </div>
        <div className="dtv-html5-c-user-default-profile-create--row">
          <div className="dtv-html5-c-user-default-profile-create--row-col_profile">
            <div className={`${selectedProfile?.isMainProfile
              ? 'dtv-html5-c-user-default-profile-create--row-col_profile-divAdmin'
              : 'dtv-html5-c-user-default-profile-create--row-col_profile-div'}`}
            >
              <CardCreate
                color={cardColor}
                isInputNotEmpty={userNameSplit}
              />
              {selectedProfile?.isMainProfile
                && (
                  <p className="dtv-html5-c-user-default-profile-create--row-col_profile-pAdmin">
                    <FormattedMessage id="profile.main.title" />
                    <br />
                    <FormattedMessage id="profile.main.subtitle" />
                  </p>
                )}
            </div>
          </div>
          <div className="dtv-html5-c-user-default-profile-create--row-col_data">
            <div className="dtv-html5-c-user-default-profile-create--row-section">
              <div className="dtv-html5-c-user-default-profile-create--row-col_data_title">
                <h2 className="dtv-html5-c-user-default-profile-create--row-col_data_title_h2">
                  <FormattedMessage id="common.data" />
                </h2>
              </div>
              {selectedProfile?.id
                ? (
                  <FocusableEditNameProfile
                    id={selectedProfile?.id}
                    profile={cardProfileName}
                    focusKey={nameProfileFocusKey}
                    indexColor={location.state?.indexColor}
                    onEnterPress={handleEditProfile}
                    focused
                  />
                ) : (
                  <FocusableEditNameProfile
                    profile={cardProfileName}
                    onEnterPress={editProfileName}
                    focused
                  />
                )}
            </div>

            <div className="dtv-html5-c-user-default-profile-create--button">
              {selectedProfile?.id
                ? (
                  <FocusableButtonProfile
                    type="submit"
                    secondGray
                    bordered
                    focused
                    className="dtv-html5-c-user-default-profile-create--button-pattern"
                    onEnterPress={clickConfirm}
                    disabled={buttonLoading}
                  >
                    <FormattedMessage id="common.confirm" />
                  </FocusableButtonProfile>
                ) : (
                  <FocusableButtonProfile
                    secondGray
                    bordered
                    focused
                    focusKey={nameProfileFocusKey}
                    onEnterPress={clickCreateProfile}
                    disabled={buttonLoading}
                  >
                    <FormattedMessage id="profile.create" />
                  </FocusableButtonProfile>
                )}
              {buttonDelete
                && (
                  <FocusableButtonProfile
                    type="submit"
                    secondGray
                    bordered
                    focused
                    className="dtv-html5-c-user-default-profile-create--button-pattern"
                    id={selectedProfile?.id}
                    indexColor={location.state.indexColor}
                    onEnterPress={handleDeleteProfile}
                  >
                    <FormattedMessage id="profile.delete" />
                  </FocusableButtonProfile>
                )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

DefaultCreateProfile.propTypes = {
  realFocusKey: PropTypes.string.isRequired,
  setFocus: PropTypes.func.isRequired,
  typeAction: PropTypes.string,
};

DefaultCreateProfile.defaultProps = {
  typeAction: '',
};

export default memo(withFocusable()(memo(DefaultCreateProfile)));
